import { useCallback, useEffect, useState } from "react";
import { useFilters } from "../../context/FilterContext";
import useApiHook from "../../api/Api";
import type { Report, ReportsResponse } from "../../../utils/interface";
import DataTable, { type TableColumn } from "react-data-table-component";
import UpdateReport from "../update/UpdateReport";
import Delete from "./actions/Delete";

const Table = () => {
  const { startDate, endDate, year, selectedFilter: filterBy } = useFilters();
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<Report | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [reportToUpdate, setReportToUpdate] = useState<Report | null>(null);
  const [data, setData] = useState<ReportsResponse | null>(null);

  const role = localStorage.getItem("role");

  const { isLoading, makeRequest } = useApiHook<ReportsResponse>("/reports", {
    method: "get",
  });

  const fetchReports = useCallback(async () => {
    setHasError(false);
    setErrorMessage("");

    const params = {
      page,
      size: perPage,
      filterBy: filterBy || "",
      start_date: startDate || "",
      end_date: endDate || "",
      year: year || "",
    };

    const response = await makeRequest(params);

    if (response?.status !== 200) {
      setHasError(true);
      setErrorMessage("An error occurred");
      setTotalRows(0);
    } else {
      setData(response?.data);
      setTotalRows(response?.data?.data?.total);
    }
  }, [page, perPage, filterBy, startDate, endDate, year, makeRequest]);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchReports();
    }
  }, [filterBy, startDate, endDate, year]);

  // Fetch when page or perPage changes
  useEffect(() => {
    fetchReports();
  }, [page, perPage]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleEdit = (row: Report) => {
    setReportToUpdate(row);
    setIsUpdateModalOpen(true);
  };

  const handleViewMore = (row: Report) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleUpdateSuccess = () => {
    setIsUpdateModalOpen(false);
    setReportToUpdate(null);
    fetchReports();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleExportCSV = () => {
    // Prepare the data
    const dataToExport = data?.data?.data || [];

    // Create CSV header
    const csvHeader = [
      "Report Date",
      "Total Sent",
      "Total Delivered",
      "Total Failed",
      "Total Pending",
      "Invalid",
      "Delivered to Network",
      "Absent from Network",
      "sms_class",
    ];

    // Create CSV rows
    const csvRows = dataToExport.map((row) => [
      formatDate(row.report_date),
      row.total_sent,
      row.total_delivered,
      row.total_failed,
      row.total_pending,
      row.invalid,
      row.total_delivered_to_network,
      row.total_absent_from_network,
      row.sms_class,
    ]);

    // Combine header and rows
    const csvContent = [
      csvHeader.join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reports-export-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const LoadingComponent = () => (
    <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
    </div>
  );

  const baseColumns: TableColumn<Report>[] = [
    {
      name: "Report Date",
      selector: (row) => row.report_date,
      sortable: true,
      minWidth: "140px",
      cell: (row) => (
        <div className="font-medium text-gray-900">
          {formatDate(row.report_date)}
        </div>
      ),
    },
    {
      name: "Total Sent",
      selector: (row) => row.total_sent,
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <div className="text-center font-semibold text-blue-600">
          {row.total_sent?.toLocaleString()}
        </div>
      ),
    },
    {
      name: "Total Delivered",
      selector: (row) => row.total_delivered,
      sortable: true,
      minWidth: "140px",
      cell: (row) => (
        <div className="text-center font-semibold text-green-600">
          {row.total_delivered?.toLocaleString()}
        </div>
      ),
    },
    {
      name: "Total Failed",
      selector: (row) => row.total_failed,
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <div className="text-center font-semibold text-red-600">
          {row.total_failed?.toLocaleString()}
        </div>
      ),
    },
    {
      name: "SMS Class",
      selector: (row) => row.sms_class,

      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <div className="text-center font-semibold text-red-600">
          {row.sms_class}
        </div>
      ),
    },
  ];

  const actionsColumn: TableColumn<Report> = {
    name: "Actions",
    minWidth: "450px",
    cell: (row) => (
      <div className="flex space-x-2">
        <button
          onClick={() => handleViewMore(row)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm"
        >
          View More
        </button>

        {role === "admin" && (
          <button
            onClick={() => handleEdit(row)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm"
          >
            Edit
          </button>
        )}
        {role === "admin" && <Delete id={row?.id} />}
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
  };

  const columns = [...baseColumns, actionsColumn];

  const customStyles = {
    table: {
      style: {
        minWidth: "900px",
        borderRadius: "0.5rem",
        overflow: "hidden",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      },
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        fontSize: "14px",
        fontWeight: "600",
        color: "#1F2937",
        backgroundColor: "#F3F4F6",
        borderBottom: "1px solid #E5E7EB",
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        fontSize: "14px",
        borderBottom: "1px solid #F3F4F6",
      },
    },
    rows: {
      style: {
        backgroundColor: "white",
        "&:hover": {
          backgroundColor: "#F9FAFB",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #E5E7EB",
        backgroundColor: "#F9FAFB",
      },
      pageButtonsStyle: {
        borderRadius: "0.375rem",
        height: "32px",
        width: "32px",
        padding: "0",
        margin: "0 4px",
        cursor: "pointer",
        transition: "0.2s ease-out",
      },
    },
  };

  return (
    <main>
      <section>
        {hasError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error</p>
            <p className="text-sm">{errorMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex justify-end mb-4">
              <button
                onClick={handleExportCSV}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export to CSV
              </button>
            </div>
            <DataTable
              columns={columns}
              data={data?.data?.data || []}
              progressPending={isLoading}
              progressComponent={<LoadingComponent />}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationPerPage={perPage}
              paginationRowsPerPageOptions={[10, 20, 30, 50]}
              highlightOnHover
              responsive
              persistTableHead
              customStyles={customStyles}
              noDataComponent={
                <div className="p-6 text-center text-gray-500">
                  No Reports found
                </div>
              }
            />
          </div>
        )}

        {/* Modal for View More Details */}
        {isModalOpen && selectedRow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-xl font-bold text-gray-800">
                  Report Details
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">
                      Basic Information
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Report Date:
                      </span>
                      <span className="text-gray-900 font-medium">
                        {formatDate(selectedRow.report_date)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Total Sent:
                      </span>
                      <span className="font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        {selectedRow.total_sent?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Total Delivered:
                      </span>
                      <span className="font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        {selectedRow.total_delivered?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Total Failed:
                      </span>
                      <span className="font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                        {selectedRow.total_failed?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">
                      Additional Details
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Total Pending:
                      </span>
                      <span className="font-semibold text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
                        {selectedRow.total_pending?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Invalid:
                      </span>
                      <span className="font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                        {selectedRow.invalid?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Delivered to Network:
                      </span>
                      <span className="font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                        {selectedRow.total_delivered_to_network?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Absent from Network:
                      </span>
                      <span className="font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                        {selectedRow.total_absent_from_network?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end p-6 border-t bg-gray-50 gap-3">
                <button
                  onClick={closeModal}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Modal */}
        {isUpdateModalOpen && reportToUpdate && (
          <UpdateReport
            report={reportToUpdate}
            isOpen={isUpdateModalOpen}
            onClose={() => setIsUpdateModalOpen(false)}
            onSuccess={handleUpdateSuccess}
          />
        )}
      </section>
    </main>
  );
};

export default Table;
